import { Component } from '@theme/component';
import { scrollIntoView } from '@theme/scrolling';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Tab anchor component - Tab 锚点滚动组件
 * 点击 tab 时滚动到对应的内容区域
 *
 * @extends {Component}
 */
export default class TabAnchorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    // 使用 setTimeout 确保 DOM 完全加载
    setTimeout(() => {
      this.#initialize();
    }, 0);
  }

  #initialize() {
    this.panels = this.querySelectorAll('.tab-anchor__panel');
    this.tabsContainer = this.querySelector('.tab-anchor__tabs');
    this.sticky = this.dataset.sticky === 'true';
    this.blockId = this.dataset.blockId;
    
    // 读取滚动偏移量配置（PC 和移动端分别配置）
    const offsetDesktop = parseInt(this.dataset.offsetDesktop || '80', 10);
    const offsetMobile = parseInt(this.dataset.offsetMobile || '80', 10);
    this.scrollOffsetDesktop = offsetDesktop;
    this.scrollOffsetMobile = offsetMobile;

    // 如果没有找到 panel，等待一下再重试（可能是动态加载的）
    if (this.panels.length === 0) {
      setTimeout(() => {
        this.panels = this.querySelectorAll('.tab-anchor__panel');
        if (this.panels.length > 0) {
          this.#generateTabs();
          this.#setupTabs();
        }
      }, 100);
    } else {
      this.#generateTabs();
      this.#setupTabs();
    }
  }


  /**
   * 从 panel 中动态生成 tab 按钮
   */
  #generateTabs() {
    if (!(this.tabsContainer instanceof HTMLElement)) {
      return;
    }

    // 清空现有的 tab 按钮
    this.tabsContainer.innerHTML = '';

    // 为每个 panel 创建一个 tab 按钮
    this.panels.forEach((panel, index) => {
      if (!(panel instanceof HTMLElement)) return;

      const tabId = panel.dataset.tabId;
      const tabLabel = panel.dataset.tabLabel || 'Tab';
      const isFirst = index === 0;

      // 生成 tab 和 panel 的 ID
      const tabButtonId = `tab-${this.blockId}-${tabId}`;
      const panelId = `panel-${this.blockId}-${tabId}`;

      // 设置 panel 的 ID（如果还没有）
      if (!panel.id) {
        panel.id = panelId;
      }

          // 如果不是第一个 tab，先添加分隔符
          if (!isFirst) {
            const separator = document.createElement('span');
            separator.className = 'tab-anchor__separator';
            separator.textContent = '/';
            separator.setAttribute('aria-hidden', 'true');
            this.tabsContainer.appendChild(separator);
          }

          // 创建 tab 按钮
          const tabButton = document.createElement('button');
          tabButton.type = 'button';
          tabButton.className = `tab-anchor__tab${isFirst ? ' is-active' : ''}`;
          tabButton.setAttribute('role', 'tab');
          tabButton.setAttribute('aria-selected', isFirst ? 'true' : 'false');
          tabButton.setAttribute('aria-controls', panelId);
          tabButton.setAttribute('data-label', tabLabel);
          tabButton.id = tabButtonId;
          tabButton.dataset.tabId = tabId;
          tabButton.dataset.panelId = panelId;
          tabButton.textContent = tabLabel;

          this.tabsContainer.appendChild(tabButton);
    });

    // 更新 tabs 引用
    this.tabs = this.querySelectorAll('.tab-anchor__tab');
  }

  #setupTabs() {
    // 初始化：设置第一个 tab 为 active（仅用于高亮，不影响 panel 显示）
    if (this.tabs.length > 0) {
      // 先移除所有 tab 的 active 状态
      this.tabs.forEach((tab) => {
        if (tab instanceof HTMLElement) {
          tab.classList.remove('is-active');
          tab.setAttribute('aria-selected', 'false');
        }
      });

      // 设置第一个 tab 为 active
      const firstTab = this.tabs[0];
      if (firstTab instanceof HTMLElement) {
        firstTab.classList.add('is-active');
        firstTab.setAttribute('aria-selected', 'true');
      }
    }

    // 绑定 tab 点击事件
    this.tabs.forEach((tab) => {
      if (tab instanceof HTMLElement) {
        tab.addEventListener('click', this.#handleTabClick.bind(this));
      }
    });

    // 监听滚动事件，实现自动高亮当前可见的 tab
    this.#setupIntersectionObserver();
  }

  /**
   * 处理 tab 点击
   * @param {Event} event
   */
  #handleTabClick(event) {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;

    const tabId = tab.dataset.tabId;
    if (!tabId) {
      console.warn('Tab ID is missing');
      return;
    }

    // 通过 data-tab-id 找到对应的 panel
    const targetPanel = this.querySelector(`.tab-anchor__panel[data-tab-id="${tabId}"]`);
    if (!(targetPanel instanceof HTMLElement)) {
      console.warn(`Panel with tab ID ${tabId} not found`);
      return;
    }

    // 更新 tab 状态
    this.#updateTabs(tab);

    // 滚动到对应的 panel
    this.#scrollToPanel(targetPanel);
  }

  /**
   * 更新 tab 状态
   * @param {HTMLElement} activeTab
   */
  #updateTabs(activeTab) {
    this.tabs.forEach((t) => {
      if (t instanceof HTMLElement) {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      }
    });

    activeTab.classList.add('is-active');
    activeTab.setAttribute('aria-selected', 'true');

    // 如果 tab 容器可以滚动，将激活的 tab 滚动到可见区域
    if (this.tabsContainer instanceof HTMLElement) {
      scrollIntoView(activeTab, {
        ancestor: this.tabsContainer,
        behavior: 'smooth',
        block: 'start',
        inline: 'center',
      });
    }
  }

  /**
   * 获取当前应该使用的滚动偏移量
   * @returns {number}
   */
  #getScrollOffset() {
    // 根据设备类型使用不同的偏移量（PC 和移动端分别配置）
    return isMobileBreakpoint() 
      ? (this.scrollOffsetMobile ?? 80)
      : (this.scrollOffsetDesktop ?? 80);
  }

  /**
   * 滚动到指定的 panel
   * @param {HTMLElement} panel
   */
  #scrollToPanel(panel) {
    // 使用 scrollIntoView，浏览器会自动处理 scroll-margin-top
    panel.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  /**
   * 设置 Intersection Observer 来监听 panel 的可见性
   * 当某个 panel 进入视口时，自动高亮对应的 tab
   */
  #setupIntersectionObserver() {
    // 如果用户偏好减少动画，则不启用自动高亮
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // 获取当前应该使用的偏移量
    const offset = this.#getScrollOffset();

    const options = {
      root: null,
      rootMargin: `-${offset}px 0px -50% 0px`,
      threshold: 0,
    };

    this.observer = new IntersectionObserver((entries) => {
      // 找到最接近顶部的可见 panel
      let topVisiblePanel = null;
      let topVisibleDistance = Infinity;

      // 获取当前应该使用的偏移量（可能在窗口大小改变时变化）
      const currentOffset = this.#getScrollOffset();

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const panel = entry.target;
          const rect = panel.getBoundingClientRect();
          const distance = Math.abs(rect.top - currentOffset);

          if (distance < topVisibleDistance) {
            topVisibleDistance = distance;
            topVisiblePanel = panel;
          }
        }
      });

      // 如果找到了可见的 panel，更新对应的 tab
      if (topVisiblePanel instanceof HTMLElement) {
        const tabId = topVisiblePanel.dataset.tabId;
        if (tabId) {
          const correspondingTab = this.querySelector(
            `.tab-anchor__tab[data-tab-id="${tabId}"]`
          );
          if (correspondingTab instanceof HTMLElement && !correspondingTab.classList.contains('is-active')) {
            this.#updateTabs(correspondingTab);
          }
        }
      }
    }, options);

    // 观察所有 panel
    this.panels.forEach((panel) => {
      if (panel instanceof HTMLElement) {
        this.observer.observe(panel);
      }
    });
  }

  /**
   * 清理资源
   */
  disconnectedCallback() {
    if (this.observer instanceof IntersectionObserver) {
      this.observer.disconnect();
    }
  }
}

if (!customElements.get('tab-anchor-component')) {
  customElements.define('tab-anchor-component', TabAnchorComponent);
}

